import { DreamBlock } from '@typedream/core';
import { focusEditor, isEditorNormalizing, normalizeEditor, toDOMNode } from '@udecode/plate-core';
import { CSSProperties, useMemo, useRef, useState } from 'react';
import { DraggableCore } from 'react-draggable';
import { Editor } from 'slate';
import { MERGING } from 'slate-history';
import { getBlockOptions, hasProperty } from '../../../editorTools/queries';
import { useTPlateEditorRef } from '../../../plate';
import { DimensionIndicator } from './DimensionIndicator';
import styles from './ResizeHandles.module.css';

type ResizeCallback = (sizes: { width?: number; height?: number }) => void;

interface ResizeHandleProps {
  direction: 's' | 'w' | 'e' | 'sw' | 'se';
  onResizeStart: () => void;
  onResize: ResizeCallback;
  onResizeStop: ResizeCallback;
  domElement: HTMLElement;
}

function ResizeHandle(props: ResizeHandleProps) {
  const { direction, domElement, onResizeStart, onResize, onResizeStop } = props;

  const rect = useRef<DOMRect>();

  const cursorStartPosition = useRef<{ x: number; y: number }>();

  const calculateNewSizes = (event: MouseEvent) => {
    const newSizes: Parameters<ResizeHandleProps['onResize']>[0] = {};

    if (
      ['s', 'sw', 'se'].includes(direction) &&
      rect.current?.height &&
      cursorStartPosition.current?.y
    ) {
      newSizes.height = rect.current.height + (event.clientY - cursorStartPosition.current.y);
    }
    if (['w', 'sw'].includes(direction) && rect.current?.width && cursorStartPosition.current?.x) {
      newSizes.width = rect.current.width + 2 * (cursorStartPosition.current.x - event.clientX);
    }
    if (['e', 'se'].includes(direction) && rect.current?.width && cursorStartPosition.current?.x) {
      newSizes.width = rect.current.width + 2 * (event.clientX - cursorStartPosition.current.x);
    }

    return newSizes;
  };

  let height: CSSProperties['height'];
  let width: CSSProperties['width'];
  let left: CSSProperties['left'];
  let right: CSSProperties['right'];
  let bottom: CSSProperties['bottom'];
  let cursor: CSSProperties['cursor'];

  if (direction === 's') {
    width = '100%';
    bottom = -5;
    cursor = 'ns-resize';
  }
  if (['sw', 'se'].includes(direction)) {
    height = 20;
    width = 20;
    bottom = -10;
  }
  if (direction === 'sw') {
    left = -10;
    cursor = 'nesw-resize';
  }
  if (direction === 'se') {
    right = -10;
    cursor = 'nwse-resize';
  }
  if (['w', 'e'].includes(direction)) {
    height = '100%';
    cursor = 'ew-resize';
  }
  if (direction === 'w') {
    left = -5;
  }
  if (direction === 'e') {
    right = -5;
  }

  return (
    <div
      style={{
        height,
        width,
        position: 'absolute',
        left,
        right,
        bottom,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <DraggableCore
        onStart={(event) => {
          rect.current = domElement.getBoundingClientRect();
          const mouseEvent = event as MouseEvent;
          cursorStartPosition.current = {
            x: mouseEvent.clientX,
            y: mouseEvent.clientY,
          };
          onResizeStart();
        }}
        onDrag={(event) => {
          const newSizes = calculateNewSizes(event as MouseEvent);
          onResize(newSizes);
        }}
        onStop={(event) => {
          const newSizes = calculateNewSizes(event as MouseEvent);
          /*
          In the lines below, when the resize event stopped, instead of using the cursor's final
          position, the width and height of the element's bounding client rect is used as the
          new size.
          */
          if (newSizes.width) newSizes.width = domElement.getBoundingClientRect().width;
          if (newSizes.height) newSizes.height = domElement.getBoundingClientRect().height;
          onResizeStop(newSizes);
        }}
      >
        <div
          role="none"
          style={{
            width: 10,
            height: 10,
            backgroundColor: 'var(--accent)',
            opacity: 0.75,
            border: '1px solid var(--light-transparent)',
            borderRadius: '50%',
            pointerEvents: 'auto',
            cursor,
          }}
        />
      </DraggableCore>
    </div>
  );
}

export interface ResizeHandlesProps {
  onResize: ResizeHandleProps['onResize'];
  element: DreamBlock;
}

export function ResizeHandles(props: ResizeHandlesProps) {
  const { onResize, element } = props;
  const editor = useTPlateEditorRef();
  const isNormalizing = useRef(true);
  const [isResizing, setIsResizing] = useState(false);

  const domElement = toDOMNode(editor, element);

  const merging = useRef<boolean | undefined>(undefined);

  const { directions, isSnapX, shouldShowDimensionIndicator, isKeepAspectRatio } = useMemo(() => {
    const blockOptions = getBlockOptions(editor, element);

    const isResizable =
      typeof blockOptions?.isResizable === 'function'
        ? blockOptions?.isResizable(element)
        : blockOptions?.isResizable;

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const directions = (typeof isResizable === 'object' && isResizable.directions) || [];

    if (directions.length === 0) {
      if (hasProperty(editor, element, 'width')) directions.push('e', 'w');
      if (hasProperty(editor, element, 'height')) directions.push('s');
    }

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const isSnapX = typeof isResizable === 'object' ? isResizable.snapX !== false : true;

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const shouldShowDimensionIndicator =
      typeof isResizable === 'object' ? !!isResizable.dimensionIndicator : false;

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const isKeepAspectRatio =
      typeof isResizable === 'object' ? !!isResizable.keepAspectRatio : false;

    return {
      directions,
      isSnapX,
      shouldShowDimensionIndicator,
      isKeepAspectRatio,
    };
  }, [editor, element]);

  if (!domElement) return null;

  const domRect = domElement.getBoundingClientRect();

  const calculateNewSizes = (
    sizes: Parameters<ResizeHandleProps['onResize']>[0],
    dir: ResizeHandleProps['direction']
  ): {
    sizes: Parameters<ResizeHandleProps['onResize']>[0];
    sizeChanged: boolean;
  } => {
    if (
      (sizes.height && sizes.height !== (element as any).height) ||
      (sizes.width && sizes.width !== (element as any).width)
    ) {
      let newSizes = sizes;
      if (isKeepAspectRatio) {
        // If keep aspect ratio, recalculate dimensions based on aspect ratio
        let newWidth = sizes.width || (element as any).width;
        let newHeight = sizes.height || (element as any).height;

        const ratio = (element as any).width / (element as any).height;

        const isVerticalOnly = dir === 's';
        const isHorizontalOnly = dir === 'e' || dir === 'w';

        if (isVerticalOnly) {
          // If vertical only, calculate width based on height
          newWidth = newHeight * ratio;
        } else if (isHorizontalOnly) {
          // If horizontal only, calculate height based on width
          newHeight = newWidth / ratio;
        } else if (ratio > 1) {
          // If landscape, calculate height based on width
          // (let the width be the limiting factor)
          newHeight = newWidth / ratio;
        } else {
          // If portrait, calculate width based on height
          // (let the height be the limiting factor)
          newWidth = newHeight * ratio;
        }

        newSizes = {
          width: newWidth,
          height: newHeight,
        };
      }
      // console.log({ sizes, newSizes, sizeChanged: true });
      return {
        sizes: newSizes,
        sizeChanged: true,
      };
    }
    // console.log({ sizes, sizeChanged: false });
    return {
      sizes,
      sizeChanged: false,
    };
  };

  return (
    <>
      {isResizing && isSnapX && <div className={styles.grid} />}

      {isResizing && shouldShowDimensionIndicator && (
        <DimensionIndicator width={Math.round(domRect.width)} height={Math.round(domRect.height)} />
      )}

      {directions.map((dir) => (
        <ResizeHandle
          key={dir}
          direction={dir}
          onResizeStart={() => {
            merging.current = MERGING.get(editor as Editor);
            setIsResizing(true);
            editor.store.setState({ isResizing: true });
            // Turn off normalization to avoid excessive computation of
            // normalization on every pixel of dragging
            isNormalizing.current = isEditorNormalizing(editor);
            Editor.setNormalizing(editor as Editor, false);
          }}
          onResize={(sizes) => {
            // FIXME: typing of element
            const { sizes: calcSizes, sizeChanged } = calculateNewSizes(sizes, dir);
            if (sizeChanged) {
              onResize(calcSizes);
            }
            MERGING.set(editor as Editor, true);
          }}
          onResizeStop={(sizes) => {
            // snap to grid
            if (sizes.width && isSnapX) {
              const { sizes: calcSizes } = calculateNewSizes(sizes, dir);
              const newSizes = {
                ...calcSizes,
                width: Math.round(sizes.width / 100) * 100,
              };
              onResize(newSizes);
            }

            MERGING.set(editor as Editor, merging.current);

            setIsResizing(false);
            editor.store.setState({ isResizing: false });

            // Restore normalization
            Editor.setNormalizing(editor as Editor, isNormalizing.current);
            normalizeEditor(editor);

            focusEditor(editor);
          }}
          domElement={domElement}
        />
      ))}
    </>
  );
}
